import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {QuarterType} from "../../../property/components/PropertyDetailGeneratedDescriptionFlat";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {AdditionalAreaType} from "../../../property/utils/get_additional_area_type";
import {IUserPreferences} from "../../../user/interfaces/IUserPreferences";
import {BoosterBuildingContentType, IChosenStats} from "../BoosterDetail";
import {IPropertyDetailBoosterStats} from "../BoosterDetail.test";
import {getBoosterAreaStats, getBoosterFloorStats, getBoosterRoomStats, getQuarterStats, getUserFilterChoice} from "../utils/get_booster_building_properties";

export interface ILocalStorageStats {
    content: BoosterBuildingContentType;
    group: string;
}

export const getRandomBoosterContent = (
    updatedBuildingParamVersions: BoosterBuildingContentType[],
    offer: IOfferDetail | IPropertyDetailOffer,
    userPref?: IUserPreferences,
    property?: IPropertyDetailBoosterStats
) => {
    const userFilterChoice = !property && userPref && getUserFilterChoice(userPref, offer);

    if (!property && !userFilterChoice) {
        return null;
    }

    // booster stats
    const offerBuildingStats = offer?.stats?.building_parameters_stats;
    const propertyBuildingStats = property?.offer.stats?.building_parameters_stats;
    const stats = property ? propertyBuildingStats : offerBuildingStats;

    if (property && !propertyBuildingStats) {
        return null;
    }

    const areaOfferStats = offer && userPref && getBoosterAreaStats(offer, userPref);
    const areaPropertyStats =
        property &&
        stats &&
        property.area > 0 &&
        (property.area < 45
            ? {...stats.properties_to_45m, type: "do 45m2"}
            : property.area < 60
              ? {...stats.properties_45m_to_60m, type: "do 60m2"}
              : {...stats.properties_over_60m, type: "powyżej 60m2"});

    const roomOfferStats = offer && userPref && getBoosterRoomStats(offer, userPref);

    let roomPropertyStats: IChosenStats | undefined;
    switch (property && property?.rooms > 0 && property.rooms) {
        case 1:
            roomPropertyStats = stats && {...stats.properties_1room, type: "z 1 pokojem"};
            break;
        case 2:
            roomPropertyStats = stats && {...stats.properties_2rooms, type: "z 2 pokojami"};
            break;
        case 3:
            roomPropertyStats = stats && {...stats.properties_3rooms, type: "z 3 pokojami"};
            break;
        case 4:
            roomPropertyStats = stats && {...stats.properties_4rooms, type: "z 4 pokojami"};
            break;
        case 5:
            roomPropertyStats = stats && {...stats.properties_5rooms_or_more, type: "z 5 pokojami lub więcej"};
    }

    const floorOfferStats = offer && userPref && getBoosterFloorStats(offer, userPref);
    const floorPropertyStats =
        stats &&
        property &&
        property.floor &&
        (property.floor <= 4
            ? {...stats.properties_on_low_floor, type: "na niskim piętrze"}
            : property.floors - property.floor === 0 || property.floors - property.floor === 1
              ? {...stats.properties_on_high_floors, type: "na wysokim piętrze"}
              : {...stats.properties_on_middle_floors, type: "na średnim piętrze"});
    const garageOfferStats = stats && {
        left: stats.properties_with_garage?.left,
        total: stats.properties_with_garage?.total,
        type: "z garażem"
    };
    const garagePropertyStats =
        property && stats && property.garages && property.garages > 0 && stats.properties_with_garage
            ? {...stats.properties_with_garage, type: "z garażem"}
            : undefined;
    const balconyOfferStats = stats && {...stats.properties_with_balconies, type: "z balkonem"};
    const balconyPropertyStats =
        stats && property && property.additional_areas && property.additional_areas.find((elem) => elem.type === AdditionalAreaType.BALCONY)
            ? {...stats.properties_with_balconies, type: "z balkonem"}
            : undefined;
    const terraceOfferStats = stats && {...stats.properties_with_terraces, type: "z tarasem"};
    const terracePropertyStats =
        stats && property && property.additional_areas[0] && property.additional_areas.find((elem) => elem.type === AdditionalAreaType.TERRACE)
            ? {...stats.properties_with_terraces, type: "z tarasem"}
            : undefined;
    const northQuarter = getQuarterStats(QuarterType.NORTH, offer, property);
    const southQuarter = getQuarterStats(QuarterType.SOUTH, offer, property);
    const westQuarter = getQuarterStats(QuarterType.WEST, offer, property);
    const eastQuarter = getQuarterStats(QuarterType.EAST, offer, property);

    let chosenStat: IChosenStats | undefined;
    let remainingChoices = [...updatedBuildingParamVersions];

    if (property) {
        if (!garagePropertyStats) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "garage");
        }
        if (!balconyPropertyStats) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "balcony");
        }
        if (!terracePropertyStats) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "terrace");
        }
        if (!northQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "north");
        }
        if (!southQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "south");
        }
        if (!westQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "west");
        }
        if (!eastQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "east");
        }
    }

    let selectedOption = null;
    // pick random content and check validity, if invalid, pick another one until options end
    for (let i = 1; i <= remainingChoices.length + (i - 1); i++) {
        const randomContentIndex = Math.floor(Math.random() * remainingChoices.length);
        const tempBoosterContent = {
            content:
                offer && userFilterChoice
                    ? (userFilterChoice as BoosterBuildingContentType)
                    : (remainingChoices[randomContentIndex] as BoosterBuildingContentType),
            group: "building_param"
        };

        const statsMap = {
            area: property ? areaPropertyStats : areaOfferStats,
            garage: property ? garagePropertyStats : garageOfferStats,
            rooms: property ? roomPropertyStats : roomOfferStats,
            floor: property ? floorPropertyStats : floorOfferStats,
            terrace: property ? terracePropertyStats : terraceOfferStats,
            balcony: property ? balconyPropertyStats : balconyOfferStats,
            north: northQuarter,
            south: southQuarter,
            east: eastQuarter,
            west: westQuarter
        };

        chosenStat = statsMap[tempBoosterContent.content] || undefined;

        if (remainingChoices.length === 0 || (remainingChoices.length === 1 && (chosenStat?.left === undefined || chosenStat?.total === undefined))) {
            return null;
        }

        // if user choice is not valid then return null
        if (chosenStat && userFilterChoice && chosenStat.total && chosenStat.left && (chosenStat.left > 2 || chosenStat.total <= 2)) {
            break;
        }

        // remove not valid elem from remainingChoices

        if (
            (chosenStat && remainingChoices.length > 0 && chosenStat.left && chosenStat.total && (chosenStat.left > 2 || chosenStat.total <= 2)) ||
            (chosenStat && chosenStat.left === undefined) ||
            !chosenStat
        ) {
            remainingChoices = remainingChoices.filter((elem) => elem !== tempBoosterContent.content);
        }
        // If a valid choice is found, set it as valid
        if (chosenStat && chosenStat.total && (chosenStat.left === 1 || chosenStat.left === 2) && chosenStat.total > 2) {
            selectedOption = {stats: chosenStat, tempBoosterContent};
            break;
        }
    }
    return selectedOption;
};
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {QuarterType} from "../../../property/components/PropertyDetailGeneratedDescriptionFlat";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {AdditionalAreaType} from "../../../property/utils/get_additional_area_type";
import {IUserPreferences} from "../../../user/interfaces/IUserPreferences";
import {BoosterBuildingContentType, IChosenStats} from "../BoosterDetail";
import {IPropertyDetailBoosterStats} from "../BoosterDetail.test";
import {getBoosterAreaStats, getBoosterFloorStats, getBoosterRoomStats, getQuarterStats, getUserFilterChoice} from "../utils/get_booster_building_properties";

export interface ILocalStorageStats {
    content: BoosterBuildingContentType;
    group: string;
}

export const getRandomBoosterContent = (
    updatedBuildingParamVersions: BoosterBuildingContentType[],
    offer: IOfferDetail | IPropertyDetailOffer,
    userPref?: IUserPreferences,
    property?: IPropertyDetailBoosterStats
) => {
    const userFilterChoice = !property && userPref && getUserFilterChoice(userPref, offer);

    if (!property && !userFilterChoice) {
        return null;
    }

    // booster stats
    const offerBuildingStats = offer?.stats?.building_parameters_stats;
    const propertyBuildingStats = property?.offer.stats?.building_parameters_stats;
    const stats = property ? propertyBuildingStats : offerBuildingStats;

    if (property && !propertyBuildingStats) {
        return null;
    }

    const areaOfferStats = offer && userPref && getBoosterAreaStats(offer, userPref);
    const areaPropertyStats =
        property &&
        stats &&
        property.area > 0 &&
        (property.area < 45
            ? {...stats.properties_to_45m, type: "do 45m2"}
            : property.area < 60
              ? {...stats.properties_45m_to_60m, type: "do 60m2"}
              : {...stats.properties_over_60m, type: "powyżej 60m2"});

    const roomOfferStats = offer && userPref && getBoosterRoomStats(offer, userPref);

    let roomPropertyStats: IChosenStats | undefined;
    switch (property && property?.rooms > 0 && property.rooms) {
        case 1:
            roomPropertyStats = stats && {...stats.properties_1room, type: "z 1 pokojem"};
            break;
        case 2:
            roomPropertyStats = stats && {...stats.properties_2rooms, type: "z 2 pokojami"};
            break;
        case 3:
            roomPropertyStats = stats && {...stats.properties_3rooms, type: "z 3 pokojami"};
            break;
        case 4:
            roomPropertyStats = stats && {...stats.properties_4rooms, type: "z 4 pokojami"};
            break;
        case 5:
            roomPropertyStats = stats && {...stats.properties_5rooms_or_more, type: "z 5 pokojami lub więcej"};
    }

    const floorOfferStats = offer && userPref && getBoosterFloorStats(offer, userPref);
    const floorPropertyStats =
        stats &&
        property &&
        property.floor &&
        (property.floor <= 4
            ? {...stats.properties_on_low_floor, type: "na niskim piętrze"}
            : property.floors - property.floor === 0 || property.floors - property.floor === 1
              ? {...stats.properties_on_high_floors, type: "na wysokim piętrze"}
              : {...stats.properties_on_middle_floors, type: "na średnim piętrze"});
    const garageOfferStats = stats && {
        left: stats.properties_with_garage?.left,
        total: stats.properties_with_garage?.total,
        type: "z garażem"
    };
    const garagePropertyStats =
        property && stats && property.garages && property.garages > 0 && stats.properties_with_garage
            ? {...stats.properties_with_garage, type: "z garażem"}
            : undefined;
    const balconyOfferStats = stats && {...stats.properties_with_balconies, type: "z balkonem"};
    const balconyPropertyStats =
        stats && property && property.additional_areas && property.additional_areas.find((elem) => elem.type === AdditionalAreaType.BALCONY)
            ? {...stats.properties_with_balconies, type: "z balkonem"}
            : undefined;
    const terraceOfferStats = stats && {...stats.properties_with_terraces, type: "z tarasem"};
    const terracePropertyStats =
        stats && property && property.additional_areas[0] && property.additional_areas.find((elem) => elem.type === AdditionalAreaType.TERRACE)
            ? {...stats.properties_with_terraces, type: "z tarasem"}
            : undefined;
    const northQuarter = getQuarterStats(QuarterType.NORTH, offer, property);
    const southQuarter = getQuarterStats(QuarterType.SOUTH, offer, property);
    const westQuarter = getQuarterStats(QuarterType.WEST, offer, property);
    const eastQuarter = getQuarterStats(QuarterType.EAST, offer, property);

    let chosenStat: IChosenStats | undefined;
    let remainingChoices = [...updatedBuildingParamVersions];

    if (property) {
        if (!garagePropertyStats) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "garage");
        }
        if (!balconyPropertyStats) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "balcony");
        }
        if (!terracePropertyStats) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "terrace");
        }
        if (!northQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "north");
        }
        if (!southQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "south");
        }
        if (!westQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "west");
        }
        if (!eastQuarter) {
            remainingChoices = remainingChoices.filter((elem) => elem !== "east");
        }
    }

    let selectedOption = null;
    // pick random content and check validity, if invalid, pick another one until options end
    for (let i = 1; i <= remainingChoices.length + (i - 1); i++) {
        const randomContentIndex = Math.floor(Math.random() * remainingChoices.length);
        const tempBoosterContent = {
            content:
                offer && userFilterChoice
                    ? (userFilterChoice as BoosterBuildingContentType)
                    : (remainingChoices[randomContentIndex] as BoosterBuildingContentType),
            group: "building_param"
        };

        const statsMap = {
            area: property ? areaPropertyStats : areaOfferStats,
            garage: property ? garagePropertyStats : garageOfferStats,
            rooms: property ? roomPropertyStats : roomOfferStats,
            floor: property ? floorPropertyStats : floorOfferStats,
            terrace: property ? terracePropertyStats : terraceOfferStats,
            balcony: property ? balconyPropertyStats : balconyOfferStats,
            north: northQuarter,
            south: southQuarter,
            east: eastQuarter,
            west: westQuarter
        };

        chosenStat = statsMap[tempBoosterContent.content] || undefined;

        if (remainingChoices.length === 0 || (remainingChoices.length === 1 && (chosenStat?.left === undefined || chosenStat?.total === undefined))) {
            return null;
        }

        // if user choice is not valid then return null
        if (chosenStat && userFilterChoice && chosenStat.total && chosenStat.left && (chosenStat.left > 2 || chosenStat.total <= 2)) {
            break;
        }

        // remove not valid elem from remainingChoices

        if (
            (chosenStat && remainingChoices.length > 0 && chosenStat.left && chosenStat.total && (chosenStat.left > 2 || chosenStat.total <= 2)) ||
            (chosenStat && chosenStat.left === undefined) ||
            !chosenStat
        ) {
            remainingChoices = remainingChoices.filter((elem) => elem !== tempBoosterContent.content);
        }
        // If a valid choice is found, set it as valid
        if (chosenStat && chosenStat.total && (chosenStat.left === 1 || chosenStat.left === 2) && chosenStat.total > 2) {
            selectedOption = {stats: chosenStat, tempBoosterContent};
            break;
        }
    }
    return selectedOption;
};
